"use client";

import { useEffect } from "react";

import { captureActivity, type ActivityEvent } from "@/lib/activity-client";

type ArtifactActivityTrackerProps = {
  artifactType: "niche" | "plan" | "brand";
  artifactId: string;
  sessionId?: string | null;
  metadata?: ActivityEvent["metadata"];
};

const READ_SECONDS = 20_000;
const READ_SCROLL_DEPTH = 0.65;

function currentScrollDepth(): number {
  const doc = document.documentElement;
  const scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
  return Math.min(1, Math.max(0, window.scrollY / scrollable));
}

export function ArtifactActivityTracker({
  artifactType,
  artifactId,
  sessionId,
  metadata,
}: ArtifactActivityTrackerProps) {
  useEffect(() => {
    if (!artifactId) return;

    const base = {
      artifactType,
      artifactId,
      sessionId: sessionId ?? undefined,
      route: window.location.pathname,
      metadata,
    };
    captureActivity({
      eventType: "artifact.opened",
      ...base,
    });

    let marked = false;
    const markReadEnough = (reason: "time" | "scroll") => {
      if (marked) return;
      marked = true;
      captureActivity({
        eventType: "artifact.read_enough",
        ...base,
        metadata: { ...metadata, read_reason: reason },
      });
      cleanup();
    };

    const onScroll = () => {
      if (currentScrollDepth() >= READ_SCROLL_DEPTH) {
        markReadEnough("scroll");
      }
    };

    const timer = window.setTimeout(() => {
      if (document.visibilityState === "visible") {
        markReadEnough("time");
      }
    }, READ_SECONDS);

    const cleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return cleanup;
  }, [artifactId, artifactType, metadata, sessionId]);

  return null;
}
